/**
 * Geocoding for the circuit leg picker (frais de déplacement).
 *
 * Photon first, Nominatim as fallback.
 *
 * Photon is OSM data served through a search engine built for typeahead, so it tolerates
 * typos — « Knitra » finds Kénitra, which Nominatim returns nothing for. Nominatim's own
 * policy also forbids autocomplete, while Photon's whole purpose is it. Nominatim is kept
 * underneath so a Photon outage degrades to the previous behaviour instead of breaking
 * search outright.
 *
 * Only place lookup lives here. Distances still come from OSRM, and nothing in the cost
 * calculation reads a place name: legs store ville_* as plain text alongside lat/lng.
 */

/** Morocco, as Photon has no countrycodes parameter — without it « Tanjer » matches Algeria. */
const MOROCCO_BBOX = '-17.5,20.5,-0.9,36.2';
/** Rough centre, to bias Photon's ranking toward local results. */
const MOROCCO_CENTRE = { lat: 31.7917, lon: -7.0926 };

const PHOTON_SEARCH = 'https://photon.komoot.io/api/';
const PHOTON_REVERSE = 'https://photon.komoot.io/reverse';
const NOMINATIM_SEARCH = 'https://nominatim.openstreetmap.org/search';
const NOMINATIM_REVERSE = 'https://nominatim.openstreetmap.org/reverse';

const REQUEST_TIMEOUT = 8000;

/** fetch with a timeout, honouring an external abort signal too. */
const fetchJson = async (url, signal) => {
  const controller = new AbortController();
  const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT);
  const onAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) controller.abort();
    signal.addEventListener('abort', onAbort);
  }
  try {
    const res = await fetch(url, { signal: controller.signal });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    return await res.json();
  } finally {
    clearTimeout(timer);
    if (signal) signal.removeEventListener('abort', onAbort);
  }
};

/** True when a rejection is just us cancelling a superseded keystroke. */
export const isAbort = (err) => err && (err.name === 'AbortError' || err.code === 20);

/**
 * Photon splits a place into name + administrative context; rebuild the one-line
 * description Nominatim gave in display_name so the dropdown reads the same either way.
 */
const photonContext = (p) => {
  const parts = [p.city, p.county, p.district, p.state, p.country]
    .filter(Boolean)
    // « Kénitra, Kénitra, Rabat-Salé-Kénitra » reads badly; keep each level once.
    .filter((v, i, arr) => arr.indexOf(v) === i && v !== p.name);
  return parts.join(', ');
};

const fromPhoton = (feature) => {
  const p = feature.properties || {};
  const [lng, lat] = feature.geometry?.coordinates || [];
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;

  const name = p.name || p.city || p.street || p.state || '';
  if (!name) return null;

  const context = photonContext(p);
  return {
    label: name,
    display_name: context ? `${name}, ${context}` : name,
    lat,
    lng,
    source: 'photon',
  };
};

const fromNominatim = (r) => {
  const lat = parseFloat(r.lat);
  const lng = parseFloat(r.lon);
  if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
  return {
    label: String(r.display_name || '').split(',')[0],
    display_name: r.display_name,
    lat,
    lng,
    source: 'nominatim',
  };
};

/**
 * Search places by name. Returns [] for no match — the caller decides how to say so.
 * Throws only on abort; a provider failure falls through to the other one.
 */
export const searchPlaces = async (query, signal) => {
  const q = (query || '').trim();
  if (q.length < 3) return [];

  // Photon: typo-tolerant, built for typeahead.
  try {
    const url = `${PHOTON_SEARCH}?q=${encodeURIComponent(q)}&limit=6&lang=fr`
      + `&bbox=${MOROCCO_BBOX}&lat=${MOROCCO_CENTRE.lat}&lon=${MOROCCO_CENTRE.lon}`;
    const data = await fetchJson(url, signal);
    // An empty answer is an answer: Photon matches more loosely than Nominatim, so if it
    // finds nothing the place almost certainly isn't there. Returning [] here avoids a
    // second request on every mistyped query, and lets the caller say "aucun lieu trouvé"
    // rather than "recherche indisponible".
    return (data?.features || []).map(fromPhoton).filter(Boolean);
  } catch (err) {
    if (isAbort(err)) throw err;
    console.warn('Photon search unavailable, falling back to Nominatim:', err.message);
  }

  // Nominatim: only when Photon actually failed, so an outage degrades to the old behaviour.
  try {
    const url = `${NOMINATIM_SEARCH}?format=json&q=${encodeURIComponent(q)}`
      + `&countrycodes=ma&accept-language=fr&limit=6`;
    const data = await fetchJson(url, signal);
    return (Array.isArray(data) ? data : []).map(fromNominatim).filter(Boolean);
  } catch (err) {
    if (isAbort(err)) throw err;
    console.warn('Nominatim search failed too:', err.message);
    throw err;
  }
};

/**
 * Label for a clicked point. Returns '' rather than throwing: the marker is already
 * placed and a missing label is not worth losing the click over.
 */
export const reverseGeocode = async (lat, lng, signal) => {
  try {
    const data = await fetchJson(`${PHOTON_REVERSE}?lat=${lat}&lon=${lng}&limit=1&lang=fr`, signal);
    const p = data?.features?.[0]?.properties;
    // Photon reverse returns the nearest FEATURE, which in a city centre is a shop or a
    // hotel — labelling a leg "Kenzi Tower Hotel Sky 28" would be useless. Administrative
    // levels come first, and the feature's own name is never used.
    const label = p && (p.city || p.locality || p.district || p.county || p.state);
    if (label) return label;
    // Outside any locality Photon returns no feature at all; Nominatim still names the
    // surrounding area, so an empty answer falls through rather than giving up.
  } catch (err) {
    if (isAbort(err)) throw err;
    console.warn('Photon reverse unavailable, falling back to Nominatim:', err.message);
  }

  try {
    const data = await fetchJson(
      `${NOMINATIM_REVERSE}?format=json&lat=${lat}&lon=${lng}&zoom=10&accept-language=fr`,
      signal
    );
    return data?.address?.city || data?.address?.town || data?.address?.village
      || String(data?.display_name || '').split(',')[0] || '';
  } catch (err) {
    if (isAbort(err)) throw err;
    console.warn('Nominatim reverse failed too:', err.message);
    return '';
  }
};
